import moment from "moment";
import { EditorState, ContentState } from "draft-js";
import htmlToDraft from "html-to-draftjs";

// Shared by the risk-stratification detail page and every screen that embeds
// its AI evaluation (the LTR case page, for one). Keep these pure — no service
// calls — so any consumer can import them without side effects.

export const RISK_LEVEL_CLASS_MAP = {
  1: "life-threatening",
  2: "very-high",
  3: "high",
  4: "moderate",
  5: "low",
};

export const RISK_LEVEL_LABEL_MAP = {
  1: "Life Threatening Risk",
  2: "Very High Risk",
  3: "High Risk",
  4: "Moderate Risk",
  5: "Low Risk",
};

// Ordered for the "Update risk level" select: lowest risk first.
export const RISK_LEVEL_OPTIONS = [5, 4, 3, 2, 1].map((value) => ({
  value,
  label: RISK_LEVEL_LABEL_MAP[value],
}));

// Evaluation status as the risk service stores it.
export const EVALUATION_STATUS_PENDING = 1;
export const EVALUATION_STATUS_UNDER_REVIEW = 2;
export const EVALUATION_STATUS_CORRECT = 3;
export const EVALUATION_STATUS_INCORRECT = 4;

export const AUDIT_EVENT_SUMMARY_UPDATED = "evaluation_summary_updated";

// Toolbar shared by the three evaluation editors.
export const EVALUATION_EDITOR_TOOLBAR = {
  options: [
    "inline",
    "blockType",
    "fontSize",
    "fontFamily",
    "list",
    "textAlign",
    "link",
    "image",
    "history",
  ],
  inline: {
    options: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "monospace",
      "superscript",
      "subscript",
    ],
  },
  list: {
    options: ["unordered", "ordered", "indent", "outdent"],
  },
};

export function getRiskClass(level) {
  return RISK_LEVEL_CLASS_MAP[level] || "";
}

export function getRiskLabel(level) {
  return RISK_LEVEL_LABEL_MAP[level] || "";
}

export function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function riskSummaryToHtml(summary) {
  const text = (summary || "").trim();
  if (!text) return "";
  return `<p>${escapeHtml(text)}</p>`;
}

export function keyDriversToHtml(drivers) {
  if (!Array.isArray(drivers) || drivers.length === 0) return "";
  const items = drivers
    .map((d) => (d == null ? "" : String(d).trim()))
    .filter(Boolean)
    .map((d) => `<li>${escapeHtml(d)}</li>`)
    .join("");
  return items ? `<ul>${items}</ul>` : "";
}

export function recommendedActionsToHtml(actions) {
  if (!Array.isArray(actions) || actions.length === 0) return "";
  const items = actions
    .map((a) => {
      const text = (a?.action || "").trim();
      if (!text) return "";
      const tags = Array.isArray(a?.tags)
        ? a.tags.filter(Boolean).map((t) => escapeHtml(String(t))).join(", ")
        : "";
      return tags
        ? `<li>${escapeHtml(text)} <em>(${tags})</em></li>`
        : `<li>${escapeHtml(text)}</li>`;
    })
    .filter(Boolean)
    .join("");
  return items ? `<ul>${items}</ul>` : "";
}

export function htmlToEditorState(html) {
  if (!html) return null;
  const block = htmlToDraft(html);
  if (!block) return null;
  return EditorState.createWithContent(
    ContentState.createFromBlockArray(block.contentBlocks),
  );
}

export function getTagClass(tag) {
  if (!tag) return "";
  const s = tag.toLowerCase();
  if (s.includes("critical")) return "tag-critical";
  if (s.includes("elevated")) return "tag-elevated";
  if (s.includes("low")) return "tag-low";
  if (s.includes("normal")) return "tag-normal";
  return "tag-default";
}

export function getTrendIcon(tag) {
  if (!tag) return "trending_flat";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "trending_up";
  if (s.includes("low")) return "trending_down";
  return "trending_flat";
}

export function getTrendClass(tag) {
  if (!tag) return "";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "trend-up";
  if (s.includes("low")) return "trend-down";
  return "";
}

export function getValueColorClass(tag) {
  if (!tag) return "value-default";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "value-elevated";
  if (s.includes("low")) return "value-lower";
  if (s.includes("normal")) return "value-normal";
  return "value-default";
}

export function getPriorityClass(tags) {
  if (!tags || tags.length === 0) return "";
  const joined = tags.join(" ").toLowerCase();
  if (joined.includes("critical")) return "priority-critical";
  if (joined.includes("high")) return "priority-high";
  return "priority-default";
}

// Key drivers arrive as plain strings, and the AI usually appends a severity
// ("Severely elevated LDL (190 mg/dL) - Critical"). Split that suffix off so
// it can render as a tag instead of trailing text. Strings without one come
// back unchanged with an empty severity.
const DRIVER_SEVERITY_RE =
  /\s*(?:[-–—:]\s*|\()(critical|very high|high|elevated|urgent|moderate|medium|borderline|low)\)?\s*$/i;

export function splitDriverSeverity(driver) {
  const text = driver == null ? "" : String(driver).trim();
  const match = text.match(DRIVER_SEVERITY_RE);
  if (!match) return { text, severity: "" };
  const severity = match[1].replace(/\b\w/g, (c) => c.toUpperCase());
  return { text: text.slice(0, match.index).trim(), severity };
}

export function getSeverityClass(severity) {
  const s = (severity || "").toLowerCase();
  if (!s) return "";
  if (s.includes("critical")) return "severity-critical";
  if (s.includes("high") || s.includes("elevated") || s.includes("urgent"))
    return "severity-high";
  if (s.includes("low")) return "severity-low";
  return "severity-moderate";
}

export function getPatientAge(info) {
  if (!info) return null;
  const dob = info.dateOfBirth || info.dob;
  if (!dob) return null;
  const m = moment(dob);
  if (!m.isValid()) return null;
  return moment().diff(m, "years");
}

export function getPatientGender(info) {
  if (!info) return "";
  if (info.gender === 1 || info.gender === "1") return "Female";
  if (info.gender === 2 || info.gender === "2") return "Male";
  if (typeof info.gender === "string") return info.gender;
  return "";
}

export function getPatientBMI(info) {
  if (!info) return "";
  if (info.bmi) return Number(info.bmi).toFixed(1);
  const heightCm = parseFloat(info.height);
  const weightKg = parseFloat(info.weight);
  if (!heightCm || !weightKg) return "";
  const m = heightCm / 100;
  if (m <= 0) return "";
  return (weightKg / (m * m)).toFixed(1);
}
